import React, { useState, useMemo,useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import AcceptOutwardRequestDialog from "./AcceptOutwardRequestDialog";  
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import posService from "../../services/pos/pos.service";

interface OutwardRequest {
  id: number;
  productId: number;
  productName: string;
  requestFromPOS: number;
  requestFromPosCode: string;
  requestToPOS: number;
  requestToPosCode: string;
  quantity: number;
  statusId: number;
  status: string;
  requestedBy: number;
  requestedDate: string;
  respondedBy: number | null;
  respondedDate: string | null;
}

interface OutwardRequestsTabProps {
  data: OutwardRequest[];
  loading: boolean;
  onRefresh: () => void;
  selectedPosIds?: number[];
}

const OutwardRequestsTab: React.FC<OutwardRequestsTabProps> = ({
  data,
  loading,
  onRefresh,
  selectedPosIds = [],
}) => {
  const toast = useRef<Toast>(null);
  const [keywordSearchOutward, setKeywordSearchOutward] = useState<string>("");
  const [acceptDialogVisible, setAcceptDialogVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<OutwardRequest | null>(null);
   const [rejectDialogVisible, setRejectDialogVisible] = useState(false);
   const [rejectNote, setRejectNote] = useState("");
  const [rejectSubmitting, setRejectSubmitting] = useState(false);

   const showMessage = (summary: string, detail: string, severity: any) => {
    toast.current?.show({
      severity,
      summary,
      detail,
      life: 3000,
    });
  };


  const handleOutwardSearch = (value: string) => {
    setKeywordSearchOutward(value);
  };

  const filteredOutwardRequests = useMemo(() => {
    if (!keywordSearchOutward) return data;

    const searchLower = keywordSearchOutward.toLowerCase();
    return data.filter(
      (request) =>
        request.productName.toLowerCase().includes(searchLower) ||
        request.requestFromPosCode.toLowerCase().includes(searchLower) ||
        request.requestToPosCode.toLowerCase().includes(searchLower) ||
        request.status.toLowerCase().includes(searchLower) ||
        request.quantity.toString().includes(searchLower)
    );
  }, [data, keywordSearchOutward]);

  const handleAccept = (request: OutwardRequest) => {
     setSelectedRequest(request);
    setAcceptDialogVisible(true);
    console.log("Accept request:", request);
    // TODO: Implement accept functionality
  };

  const handleAcceptSuccess = () => {
    setAcceptDialogVisible(false);
    setSelectedRequest(null);
    onRefresh();
  };

  const handleReject = (request: OutwardRequest) => {
     setSelectedRequest(request);
    setRejectNote("");
    setRejectDialogVisible(true);
    console.log("Reject request:", request);
    // TODO: Implement reject functionality
  };

  const handleRejectRequest = async () => {
    // Validate note is not empty
    if (!rejectNote || rejectNote.trim() === "") {
      showMessage("Validation Error", "Please enter a reason for rejection", "warn");
      return;
    }

    if (!selectedRequest) {
      showMessage("Error", "No request selected", "error");
      return;
    }

    try {
      setRejectSubmitting(true);

      const payload = {
        requestId: selectedRequest.id,
        productId: selectedRequest.productId,
        posId: selectedRequest.requestToPOS, // RequestToPOS
        toPosId: selectedRequest.requestFromPOS, // RequestFromPOS
        quantity: selectedRequest.quantity,
        note: rejectNote,
      };

      const response = await posService.UpdatePosReject(payload);

      if (response && response.isSuccess) {
        showMessage("Success", response.message || "Request rejected successfully", "success");
        setRejectDialogVisible(false);
        setSelectedRequest(null);
        setRejectNote("");
        onRefresh();
      } else {
        showMessage(
          "Error",
          response?.message || "Failed to reject request",
          "error"
        );
      }
    } catch (error: any) {
      console.error("Error rejecting request:", error);
      showMessage(
        "Error",
        error?.response?.data?.message || "Failed to reject request",
        "error"
      );
    } finally {
      setRejectSubmitting(false);
    }
  };

  const handleCloseRejectDialog = () => {
    setRejectDialogVisible(false);
    setSelectedRequest(null);
    setRejectNote("");
  };

  const OutwardRequestExport = (requests: OutwardRequest[]) => {
    console.log("Export outward requests:", requests);
    // TODO: Implement export functionality
  };

  const dateBodyTemplate = (rowData: OutwardRequest) => {
  if (!rowData.requestedDate) return "-";

  const date = new Date(rowData.requestedDate);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

  const actionBodyTemplate = (rowData: OutwardRequest) => {
    return (
      <div className="flex gap-2">
        <Button
          label="Accept"
          icon="pi pi-check"
          className="px-1 py-2 text-sm"
          onClick={() => handleAccept(rowData)}
        />
        <Button
          label="Reject"
          icon="pi pi-times"
          severity="danger"
          className="px-1 py-2 text-sm"
          onClick={() => handleReject(rowData)}
        />
      </div>
    );
  };

  const rejectDialogFooter = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Cancel"
        icon="pi pi-times"
        className="p-button-text"
        onClick={handleCloseRejectDialog}
        disabled={rejectSubmitting}
      />
      <Button
        label="Reject"
        icon="pi pi-check"
        severity="danger"
        onClick={handleRejectRequest}
        loading={rejectSubmitting}
        // disabled={!rejectNote || rejectNote.trim() === ""}
      />
    </div>
  );

  return (
    <div className="col-12">
      <Toast ref={toast} position="top-right" />
      <div className="flex justify-content-between align-items-center mb-3">
        <div className="p-input-icon-left w-full md:w-4">
          <i className="pi pi-search" />
          <InputText
            type="search"
            value={keywordSearchOutward}
            onChange={(e) => handleOutwardSearch(e.target.value)}
            placeholder="Search Outward Requests"
            className="w-full"
            style={{
              border: "1px solid #75bb8aff",
              boxShadow: "0 0 0 1px #75bb8aff",
            }}
          />
        </div>
        {/* {data.length > 0 && (
          <Button
            className="text-black border-round ml-3"
            onClick={() => OutwardRequestExport(filteredOutwardRequests)}
            icon="pi pi-file-excel"
            text
            raised
          >
            Export Outward Requests
          </Button>
        )} */}
      </div>

      {filteredOutwardRequests && filteredOutwardRequests.length > 0 ? (
        <DataTable
          value={filteredOutwardRequests}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 25, 50]}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
        >
          <Column
            field="requestedDate"
            header="Requested Date"
            body={dateBodyTemplate}
            sortable
            style={{ width: "15%" }}
          />
          <Column
            field="productName"
            header="Product Name"
            sortable
            style={{ width: "25%" }}
          />
          <Column
            field="quantity"
            header="Request Quantity"
            sortable
            style={{ width: "10%" }}
          />
          <Column
            field="requestFromPosCode"
            header="Requested By"
            sortable
            style={{ width: "12%" }}
          />
          <Column
            field="requestToPosCode"
            header="Requested To"
            sortable
            style={{ width: "12%" }}
          />
          <Column
            header="Actions"
            body={actionBodyTemplate}
            style={{ width: "16%" }}
          />
        </DataTable>
      ) : (
        <div className="text-center p-4">
          <p className="text-gray-500">
            {keywordSearchOutward
              ? "No outward requests found matching your search."
              : "No outward requests found."}
          </p>
        </div>
      )}
        <AcceptOutwardRequestDialog
        visible={acceptDialogVisible}
        onHide={() => setAcceptDialogVisible(false)}
        request={selectedRequest}
        onSuccess={handleAcceptSuccess}
      />

      <Dialog
        header="Reject Request - Add Note"
        visible={rejectDialogVisible}
        onHide={handleCloseRejectDialog}
        style={{ width: "30vw" }}
        breakpoints={{ "960px": "50vw", "641px": "90vw" }}
        modal
        footer={rejectDialogFooter}
      >
        <div className="p-fluid">
          {selectedRequest && (
            <div className="mb-3 p-3 surface-100 border-round">
              <div className="mb-2">
                <strong>Product:</strong> {selectedRequest.productName}
              </div>
              <div className="mb-2">
                <strong>Requested From:</strong> {selectedRequest.requestFromPosCode}
              </div>
              <div className="mb-2">
                <strong>Quantity:</strong> {selectedRequest.quantity}
              </div>
            </div>
          )}

          <div className="field">
            <label htmlFor="rejectNote" className="font-bold">
              Note <span className="text-red-500">*</span>
            </label>
            <InputText
              id="rejectNote"
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              placeholder="Enter reason for rejection"
              required
              maxLength={255}
              // className={rejectNote.trim() === "" ? "p-invalid" : ""}
              disabled={rejectSubmitting}
              style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
            />
            {/* {rejectNote.trim() === "" && (
              <small className="p-error">Note is required</small>
            )} */}
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default OutwardRequestsTab;
